import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Church, LogOut, Save, Trash2, Upload, Plus, ExternalLink, ShieldCheck, User as UserIcon, Users as UsersIcon, FileText, CheckCircle2, Clock, Mail, MessageSquare, Heart, BookOpen, Settings, ChevronDown, Trophy, Radio, BarChart3 } from "lucide-react";
import SectionForm from "@/components/cms/SectionForm";
import { BBC_LOGO_URL } from "@/lib/brand";
import ProfilePhotoWidget from "@/components/ProfilePhotoWidget";
import PrayerComments from "@/components/PrayerComments";
import { DailyVerseTab, DevotionTab, AnnouncementsTab, GroupsTab, BibleWarmTab, BackupsTab, MessagesVisibilityTab } from "@/pages/AdminExtraTabs";
import NotificationBell from "@/components/NotificationBell";
import AiTab from "@/pages/AiTab";
import QuizTab from "@/pages/QuizTab";
import MobileAppTab from "@/pages/MobileAppTab";
import MusicTab from "@/pages/MusicTab";
import NewsFeedTab from "@/pages/NewsFeedTab";
import TTSTab from "@/pages/TTSTab";
import SermonCMS, { ManagedSermonEntities } from "@/pages/SermonCMS";
import VisitorMessagesTab from "@/pages/VisitorMessagesTab";
import DoctrinalReviewTab from "@/pages/DoctrinalReviewTab";
import MinistryCmsTab from "@/pages/MinistryCmsTab";
import LibraryCMS from "@/pages/LibraryCMS";
import AnalyticsTab from "@/pages/AnalyticsTab";

const SECTIONS = [
  { key: "hero", label: "Hero / Pastor's Message" },
  { key: "livestream", label: "Livestream Banner" },
  { key: "schedule", label: "Schedule of Services" },
  { key: "ministries", label: "Ministries" },
  { key: "beliefs", label: "What We Believe" },
  { key: "salvation", label: "Call to Salvation" },
  { key: "giving", label: "Giving" },
  { key: "location", label: "Location & Contact" },
  { key: "footer", label: "Footer" },
];

function OfflineContentTab() {
  const [pack, setPack] = useState(null), [error, setError] = useState("");
  useEffect(() => { api.get("/offline/bible/manifest").then(({ data }) => setPack(data)).catch(() => setError("The offline pack manifest is unavailable.")); }, []);
  return <section className="rounded-xl border bg-white p-5"><h2 className="font-serif-display text-2xl">Offline Content Versions</h2>{error && <p role="alert">{error}</p>}{pack && <><p className="mt-3 break-all text-sm">Current version: {pack.offline_content_version}</p><p className="mt-2 text-sm">Pack size: {(pack.file_size / 1048576).toFixed(2)} MiB</p><p className="mt-3 text-sm">KJV, Ilocano, and historical commentaries with doctrinal cautions. Devices check this version during offline refresh and retain their current pack until an update validates.</p></>}</section>;
}

function ContentTab({ initialSection = "hero" }) {
  const [section, setSection] = useState(initialSection);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [loadedSection, setLoadedSection] = useState(null);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadedSection(null);
    api.get(`/content/${section}`).then(({ data }) => {
      if (active) { setData(data || {}); setLoadedSection(section); }
    }).catch(() => { if (active) toast.error("Could not load content. Reload the page to retry."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [section]);

  const save = async () => {
    if (loadedSection !== section || loading || saving) return;
    setSaving(true);
    try {
      await api.put(`/content/${section}`, data);
      // #region agent log
      fetch('http://127.0.0.1:7326/ingest/43a25cab-f050-49f0-855e-165f1e083422',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'a69cbb'},body:JSON.stringify({sessionId:'a69cbb',location:'AdminDashboard.jsx:save',message:'cms save ok',data:{section,keys:Object.keys(data||{})},timestamp:Date.now(),hypothesisId:'A'})}).catch(()=>{});
      // #endregion
      toast.success("Saved.");
    } catch (e) {
      // #region agent log
      fetch('http://127.0.0.1:7326/ingest/43a25cab-f050-49f0-855e-165f1e083422',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'a69cbb'},body:JSON.stringify({sessionId:'a69cbb',location:'AdminDashboard.jsx:save',message:'cms save failed',data:{section,status:e?.response?.status,detail:e?.response?.data?.detail||e?.message},timestamp:Date.now(),hypothesisId:'A'})}).catch(()=>{});
      // #endregion
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-3">
        <div className="p-3 bg-white border border-[#E5E0D8] rounded-xl space-y-1">
          {SECTIONS.filter(item => initialSection === "livestream" ? item.key === "livestream" : item.key !== "livestream").map((s) => (
            <button
              key={s.key}
              data-testid={`cms-section-${s.key}`}
              onClick={() => setSection(s.key)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${section === s.key ? "bg-[#2C4C3B] text-white" : "hover:bg-[#F3EFEA] text-[#1C201E]"}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>
      <div className="lg:col-span-9">
        <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif-display text-2xl text-[#1C201E]">{SECTIONS.find((x) => x.key === section)?.label}</h3>
            <button
              data-testid="save-content-button"
              onClick={save}
              disabled={saving || loading || loadedSection !== section}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2C4C3B] text-white text-sm hover:bg-[#1A3326] disabled:opacity-60"
            >
              <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save"}
            </button>
          </div>
          <p className="text-xs text-[#5C6661] mb-3">Long text fields support bold, italic, headings, lists, and quotes.</p>
          {loading ? <div className="text-[#5C6661]">Loading…</div> : <SectionForm section={section} value={data} onChange={setData} />}
        </div>
      </div>
    </div>
  );
}

function EventsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", date: "", time: "", location: "", description: "" });

  const load = async () => setItems((await api.get("/events")).data);
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    if (!form.title.trim() || !form.date) { toast.error("Title and date required"); return; }
    try {
      await api.post("/events", form);
      setForm({ title: "", date: "", time: "", location: "", description: "" });
      await load();
      toast.success("Event added");
    } catch { toast.error("Add failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    await api.delete(`/events/${id}`);
    await load();
  };

  return (
    <div className="space-y-6">
      <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl">
        <h3 className="font-serif-display text-2xl text-[#1C201E] mb-4">Add Event</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <input data-testid="event-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
          <input data-testid="event-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
          <input data-testid="event-time" placeholder="Time (e.g. 5:00 PM)" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
          <input data-testid="event-location" placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
        </div>
        <textarea data-testid="event-description" rows={3} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-3 w-full px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
        <button data-testid="event-add-button" onClick={add} className="mt-4 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#2C4C3B] text-white text-sm hover:bg-[#1A3326]">
          <Plus className="w-4 h-4" /> Add Event
        </button>
      </div>

      <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl">
        <h3 className="font-serif-display text-2xl text-[#1C201E] mb-4">All Events ({items.length})</h3>
        <div className="divide-y divide-[#E5E0D8]">
          {items.map((e, i) => (
            <div key={e.id} data-testid={`admin-event-${i}`} className="py-4 flex items-start justify-between gap-4">
              <div>
                <div className="font-medium">{e.title}</div>
                <div className="text-xs text-[#5C6661] mt-1">{e.date} {e.time && `· ${e.time}`} {e.location && `· ${e.location}`}</div>
                {e.description && <div className="text-sm text-[#5C6661] mt-1">{e.description}</div>}
              </div>
              <button data-testid={`event-delete-${i}`} onClick={() => del(e.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          {items.length === 0 && <div className="text-sm text-[#5C6661]">No events yet.</div>}
        </div>
      </div>
    </div>
  );
}

function CounselingTab({ role, user }) {
  const [items, setItems] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);

  const isPastor = role === "super_admin";

  const load = async () => {
    try {
      const [{ data: sessions }, { data: allUsers }] = await Promise.all([
        api.get("/counseling/assigned"),
        isPastor ? api.get("/users") : Promise.resolve({ data: [] })
      ]);
      setItems(sessions || []);
      if (isPastor) setStaff((allUsers || []).filter(u => u.role === 'staff' || u.role === 'admin'));
    } catch (e) { toast.error("Load failed"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openChat = async (s) => {
    setActiveSession(s);
    setChatLoading(true);
    try {
      const { data } = await api.get(`/counseling/${s.id}/messages`);
      setMessages(data || []);
    } catch { setMessages([]); }
    finally { setChatLoading(false); }
  };

  const assign = async (sid, staffIds) => {
    try {
      await api.put(`/counseling/${sid}/assign`, { staff_ids: staffIds });
      toast.success("Staff assigned");
      load();
    } catch { toast.error("Assignment failed"); }
  };

  const resolve = async (sid) => {
    if (!window.confirm("Resolve this session?")) return;
    try {
      await api.put(`/counseling/${sid}/resolve`);
      toast.success("Session resolved");
      load();
    } catch { toast.error("Failed to resolve"); }
  };

  if (loading) return <div className="py-10 text-center text-[#5C6661]">Loading counseling hub...</div>;

  return (
    <div className="grid lg:grid-cols-12 gap-6" data-testid="counseling-tab">
      <div className="lg:col-span-4 space-y-4">
        <h3 className="font-serif-display text-2xl text-[#1C201E]">Guidance Hub</h3>
        <div className="bg-white border border-[#E5E0D8] rounded-xl overflow-hidden divide-y divide-[#E5E0D8]">
          {items.map(s => (
            <button
              key={s.id}
              onClick={() => openChat(s)}
              className={`w-full text-left p-4 hover:bg-[#FAF9F6] transition-colors ${activeSession?.id === s.id ? 'bg-[#2C4C3B]/5 border-l-4 border-[#2C4C3B]' : ''}`}
            >
              <div className="flex justify-between items-start mb-1">
                <span className="font-bold text-sm text-[#1C201E]">{s.title}</span>
                <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${s.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{s.status.toUpperCase()}</span>
              </div>
              <div className="text-[11px] text-[#5C6661]">By {s.user_name} · {new Date(s.created_at).toLocaleDateString()}</div>
            </button>
          ))}
          {items.length === 0 && <div className="p-10 text-center text-xs text-[#8A8F8B]">No active sessions found.</div>}
        </div>
      </div>

      <div className="lg:col-span-8">
        {activeSession ? (
          <div className="bg-white border border-[#E5E0D8] rounded-xl flex flex-col h-[70vh]">
            <div className="p-4 border-b border-[#E5E0D8] flex justify-between items-center bg-[#FAF9F6]">
              <div>
                <h4 className="font-bold text-[#1C201E]">{activeSession.title}</h4>
                <p className="text-xs text-[#5C6661]">{activeSession.user_name}'s session</p>
              </div>
              <div className="flex gap-2">
                {isPastor && (
                  <StaffSelector
                    selected={activeSession.assigned_staff_ids}
                    all={staff}
                    onSelect={(ids) => assign(activeSession.id, ids)}
                  />
                )}
                {activeSession.status === 'active' && (
                  <button onClick={() => resolve(activeSession.id)} className="px-3 py-1 bg-[#2C4C3B] text-white text-xs rounded-full">Resolve</button>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
               <div className="bg-amber-50 p-4 rounded-lg border border-amber-100 mb-6">
                 <span className="text-[10px] font-bold text-amber-800 uppercase block mb-1">Initial Request</span>
                 <p className="text-sm text-amber-950 italic">"{activeSession.description}"</p>
               </div>

               {chatLoading ? <div className="text-center py-10 text-xs">Loading dialogue...</div> : messages.map(m => (
                 <div key={m.id} className={`flex flex-col ${m.user_id === user.user_id ? 'items-end' : 'items-start'}`}>
                    <span className="text-[9px] font-bold text-[#5C6661] mb-1">{m.user_name}</span>
                    <div className={`p-3 rounded-2xl max-w-[80%] text-sm ${m.user_id === user.user_id ? 'bg-[#2C4C3B] text-white' : 'bg-[#F3EFEA] text-[#1C201E]'}`}>
                      {m.text}
                    </div>
                 </div>
               ))}
            </div>
          </div>
        ) : (
          <div className="h-full border-2 border-dashed border-[#E5E0D8] rounded-2xl flex items-center justify-center text-[#5C6661] italic text-sm">
            Select a session to view dialogue and manage care.
          </div>
        )}
      </div>
    </div>
  );
}

function StaffSelector({ selected, all, onSelect }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="px-3 py-1 border border-[#E5E0D8] rounded-full text-xs hover:bg-[#F3EFEA]">
        Manage Staff ({selected.length})
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-56 bg-white border border-[#E5E0D8] rounded-xl shadow-xl z-50 p-2">
          {all.map(s => (
            <label key={s.user_id} className="flex items-center gap-2 p-2 hover:bg-[#FAF9F6] rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={selected.includes(s.user_id)}
                onChange={(e) => {
                  const next = e.target.checked ? [...selected, s.user_id] : selected.filter(id => id !== s.user_id);
                  onSelect(next);
                }}
              />
              <span className="text-xs">{s.name} ({s.role === "bible_school_student" ? "Bible School Student" : s.role})</span>
            </label>
          ))}
          <div className="border-t border-[#E5E0D8] mt-2 pt-2">
            <button onClick={() => setOpen(false)} className="w-full text-center text-[10px] font-bold text-[#2C4C3B]">DONE</button>
          </div>
        </div>
      )}
    </div>
  );
}

function PrayersTab({ role }) {
  const [items, setItems] = useState([]);
  const [kindFilter, setKindFilter] = useState("prayer_request");
  const [editingId, setEditingId] = useState(null);
  const [editRequest, setEditRequest] = useState("");
  const [openCommentsId, setOpenCommentsId] = useState(null);

  const canApprove = role === "super_admin";
  const load = async () => {
    const { data } = await api.get(`/prayers?kind=${kindFilter}${kindFilter === "message" ? "&message_source=app" : ""}`);
    setItems(data || []);
  };
  useEffect(() => { load(); }, [kindFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const del = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    await api.delete(`/prayers/${id}`);
    await load();
  };

  const setApproved = async (id, approved) => {
    try {
      await api.put(`/prayers/${id}/approve`, { approved });
      toast.success(approved ? "Praise approved for the public wall" : "Praise removed from the wall");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/prayers/${editingId}`, { request: editRequest });
      toast.success("Updated");
      setEditingId(null);
      await load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const isPraise = kindFilter === "praise";
  const isMessage = kindFilter === "message";
  const KIND_META = {
    prayer_request: { label: "Prayer Requests", hint: "Prayer requests are private and never shown on the website. They will be viewable in the BBC Connect mobile app." },
    praise: { label: "Items of Praise", hint: "Approve public-consent praises to publish them on the site's Items of Praise wall. Un-consented praises are kept private." },
    message: { label: "App Messages", hint: "Messages sent inside BBC Connect. Website correspondence is in Visitors Messages." },
  };

  return (
    <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl">
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <h3 className="font-serif-display text-2xl text-[#1C201E]">Prayer, Praise &amp; Messages</h3>
        <div className="inline-flex bg-[#F3EFEA] border border-[#E5E0D8] rounded-full p-1 flex-wrap">
          {["prayer_request", "praise", "message"].map((k) => (
            <button
              key={k}
              data-testid={`prayers-filter-${k}`}
              onClick={() => setKindFilter(k)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium ${kindFilter === k ? "bg-[#2C4C3B] text-white" : "text-[#1C201E]"}`}
            >
              {KIND_META[k].label} ({kindFilter === k ? items.length : "—"})
            </button>
          ))}
        </div>
      </div>
      <p className="text-xs text-[#5C6661] mb-4">{KIND_META[kindFilter].hint}</p>

      <div className="divide-y divide-[#E5E0D8]">
        {items.map((p, i) => {
          const publicConsent = !!p.is_public;
          const approved = !!p.approved;
          const pendingReview = isPraise && publicConsent && !approved;
          return (
            <div key={p.id} data-testid={`admin-prayer-${i}`} className="py-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{p.name}</span>
                    {isPraise && publicConsent && approved && (
                      <span data-testid={`praise-status-live-${i}`} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-[#2C4C3B]/10 text-[#2C4C3B] rounded-full"><CheckCircle2 className="w-3 h-3" /> live on wall</span>
                    )}
                    {pendingReview && (
                      <span data-testid={`praise-status-pending-${i}`} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-[#D4A373]/20 text-[#8a5a2a] rounded-full"><Clock className="w-3 h-3" /> pending review</span>
                    )}
                    {isPraise && !publicConsent && (
                      <span className="text-xs px-2 py-0.5 bg-[#E5E0D8] text-[#5C6661] rounded-full">private praise</span>
                    )}
                    {isMessage && (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-[#2C4C3B]/10 text-[#2C4C3B] rounded-full"><Mail className="w-3 h-3" /> message</span>
                    )}
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-red-50 text-red-700 rounded-full font-bold">
                      <Heart className="w-3 h-3 fill-current" /> {p.prayed_for_count || 0}
                    </span>
                  </div>
                  {p.email && (
                    isMessage
                      ? <a data-testid={`message-reply-${i}`} href={`mailto:${p.email}?subject=Re:%20Your%20message%20to%20Bible%20Baptist%20Church%20Barani`} className="text-xs text-[#2C4C3B] hover:underline">{p.email}</a>
                      : <div className="text-xs text-[#5C6661]">{p.email}</div>
                  )}

                  {editingId === p.id ? (
                    <div className="mt-2 space-y-2">
                      <textarea
                        value={editRequest}
                        onChange={e => setEditRequest(e.target.value)}
                        className="w-full p-2 border border-[#E5E0D8] rounded-lg text-sm"
                        rows={4}
                      />
                      <div className="flex gap-2">
                        <button onClick={saveEdit} className="px-3 py-1 bg-[#2C4C3B] text-white text-xs rounded-full">Save</button>
                        <button onClick={() => setEditingId(null)} className="px-3 py-1 border border-[#E5E0D8] text-xs rounded-full">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2 text-[#1C201E] whitespace-pre-line">{p.request}</div>
                  )}

                  <div className="text-xs text-[#5C6661] mt-1">{new Date(p.created_at).toLocaleString()}</div>

                  <button
                    onClick={() => setOpenCommentsId(openCommentsId === p.id ? null : p.id)}
                    className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-[#2C4C3B] hover:underline"
                  >
                    <MessageSquare className="w-3.5 h-3.5" />
                    {openCommentsId === p.id ? "Hide Discussion" : `View Discussion (${p.comment_count || 0})`}
                  </button>

                  {openCommentsId === p.id && (
                    <PrayerComments prayerId={p.id} onCommentAdded={load} />
                  )}
                </div>
                <div className="flex flex-col items-end gap-2">
                  {editingId !== p.id && (
                    <button
                      onClick={() => { setEditingId(p.id); setEditRequest(p.request); }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#E5E0D8] text-[#1C201E] text-xs hover:bg-[#F3EFEA]"
                    >
                      <Plus className="w-3 h-3 rotate-45" /> Edit
                    </button>
                  )}
                  {isPraise && publicConsent && canApprove && (
                    approved
                      ? <button data-testid={`praise-unapprove-${i}`} onClick={() => setApproved(p.id, false)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#E5E0D8] text-[#1C201E] text-xs hover:bg-[#F3EFEA]">Unapprove</button>
                      : <button data-testid={`praise-approve-${i}`} onClick={() => setApproved(p.id, true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#2C4C3B] text-white text-xs hover:bg-[#1A3326]"><CheckCircle2 className="w-3 h-3" /> Approve for wall</button>
                  )}
                  {isMessage && p.email && (
                    <a data-testid={`message-reply-btn-${i}`} href={`mailto:${p.email}?subject=Re:%20Your%20message%20to%20Bible%20Baptist%20Church%20Barani`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#2C4C3B] text-white text-xs hover:bg-[#1A3326]"><Mail className="w-3 h-3" /> Reply</a>
                  )}
                  {canApprove && (
                    <button data-testid={`prayer-delete-${i}`} onClick={() => del(p.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {items.length === 0 && <div className="text-sm text-[#5C6661]">Nothing here yet.</div>}
      </div>
    </div>
  );
}

function UsersTab({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [roleFilter, setRoleFilter] = useState("all");
  const [verificationFilter, setVerificationFilter] = useState("all");
  const [confirmAction, setConfirmAction] = useState(null);
  const [transitioning, setTransitioning] = useState(false);
  const load = async () => setUsers((await api.get("/users")).data || []);
  useEffect(() => { load(); }, []);

  const setRole = async (u, role, prefix) => {
    try {
      await api.put(`/users/${u.user_id}/role`, { role, prefix: prefix || u.prefix });
      toast.success(`${u.email} updated`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const setPrefix = async (u, prefix) => {
    try {
      await api.put(`/users/${u.user_id}/role`, { role: u.role, prefix });
      toast.success(`Prefix for ${u.name} set to ${prefix || 'Default'}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const returnToPending = async (u) => {
    setConfirmAction({ kind: "pending", user: u });
  };

  const confirmTransition = async () => {
    if (!confirmAction || transitioning) return;
    const { kind, user: target } = confirmAction;
    if (kind === "visitor") return setRole(target, "visitor");
    setTransitioning(true);
    try {
      const { data } = await api.put(`/users/${target.user_id}/return-to-pending`);
      if (data?.role !== "member" || data?.verification_status !== "pending") throw new Error("The server did not confirm the pending status.");
      await load();
      setConfirmAction(null);
      toast.success("Returned to Pending Verification");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Could not return this member to Pending Verification");
    } finally {
      setTransitioning(false);
    }
  };

  return (
    <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl">
      <h3 className="font-serif-display text-2xl text-[#1C201E] mb-1">User Roles ({users.length})</h3>
      <p className="text-xs text-[#5C6661] mb-6">Promote a member to <b>Admin</b> so they can upload sermons and events (they will not be able to edit page content). Demote back to <b>Member</b> anytime.</p>
      <div className="flex flex-wrap gap-3 mb-5">
        <select aria-label="Filter by role" value={roleFilter} onChange={e => setRoleFilter(e.target.value)} className="text-sm border rounded-lg px-3 py-2"><option value="all">All roles</option>{[["visitor","Visitor"],["member","Member"],["bible_school_student","Bible School Student"],["staff","Staff"],["admin","Admin"],["super_admin","Super Admin"]].map(([v,l])=><option key={v} value={v}>{l}</option>)}</select>
        <select aria-label="Filter by verification" value={verificationFilter} onChange={e => setVerificationFilter(e.target.value)} className="text-sm border rounded-lg px-3 py-2"><option value="all">All verification states</option><option value="verified">Verified</option><option value="pending">Pending Verification</option></select>
      </div>
      <div className="divide-y divide-[#E5E0D8]">
        {users.filter(u => (roleFilter === "all" || (u.role || (u.is_admin ? "super_admin" : "member")) === roleFilter) && (verificationFilter === "all" || (u.role !== "visitor" && (u.verification_status || (u.role === "pending" ? "pending" : "verified")) === verificationFilter))).map((u, i) => {
          const role = u.role || (u.is_admin ? "super_admin" : "member");
          const isSuper = role === "super_admin";
          const isSelf = u.user_id === currentUser?.user_id;
          const isPending = role === "pending";
          const badge = {
            super_admin: { label: "Super Admin", cls: "bg-[#2C4C3B] text-white" },
            admin: { label: "Admin", cls: "bg-[#D4A373]/30 text-[#8a5a2a]" },
            staff: { label: "Staff", cls: "bg-[#D4A373]/20 text-[#8a5a2a]" },
            bible_school_student: { label: "Bible School Student", cls: "bg-blue-100 text-blue-900" },
            member: { label: "Member", cls: "bg-[#E5E0D8] text-[#1C201E]" },
            visitor: { label: "Visitor", cls: "bg-sky-100 text-sky-900" },
            pending: { label: "Pending", cls: "bg-amber-100 text-amber-800 border border-amber-300" },
          }[role] || { label: role, cls: "bg-[#E5E0D8] text-[#1C201E]" };
          const setStatus = async (s) => {
            try { await api.put(`/users/${u.user_id}/status`, { status: s }); await load(); toast.success("Status updated"); }
            catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
          };
          const memberStatus = u.member_status || "active";
          const approve = async () => {
            try { await api.put(`/users/${u.user_id}/approve`); await load(); toast.success(`${u.name} approved`); }
            catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
          };
          const verification = role === "visitor" ? "Not Applicable" : (u.verification_status || (role === "pending" ? "pending" : "verified")) === "pending" ? "Pending Verification" : "Verified";
          return (
            <div key={u.user_id} data-testid={`user-row-${i}`} className="py-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                {u.picture ? <img src={u.picture.startsWith('/api/') ? (process.env.REACT_APP_BACKEND_URL + u.picture) : u.picture} alt="" className="w-9 h-9 rounded-full object-cover" /> : <div className="w-9 h-9 rounded-full bg-[#F3EFEA] flex items-center justify-center"><UserIcon className="w-4 h-4 text-[#5C6661]" /></div>}
                <div className="min-w-0">
                  <div className="font-medium text-[#1C201E] truncate">{u.name} {isSelf && <span className="text-xs text-[#5C6661]">(you)</span>}</div>
                  <div className="text-xs text-[#5C6661] truncate">{u.email}</div>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <select
                  data-testid={`prefix-${i}`}
                  value={u.prefix || ""}
                  onChange={(e) => setPrefix(u, e.target.value)}
                  className="text-xs px-2 py-1 rounded-full border border-[#E5E0D8] bg-white font-bold text-[#2C4C3B]"
                >
                  <option value="">Default</option>
                  <option value="Bro.">Bro.</option>
                  <option value="Sis.">Sis.</option>
                  <option value="Ptr.">Ptr.</option>
                  <option value="Pr.">Pr.</option>
                </select>
                  <span className={`text-xs px-3 py-1 rounded-full font-medium ${badge.cls}`}>{badge.label}</span>
                  <span className="text-xs text-[#5C6661]">Verification: {verification}</span>
                  <span className="text-xs text-[#5C6661]">Profile: {u.profile_complete ? "Complete" : "Incomplete"}</span>
                <select data-testid={`status-${i}`} value={memberStatus} onChange={(e) => setStatus(e.target.value)} className="text-xs px-2 py-1 rounded-full border border-[#E5E0D8] bg-white">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="gone_with_lord">Gone with the Lord</option>
                </select>
                {(isPending || (role === "member" && verification === "Pending Verification")) && !isSelf && (
                  <button data-testid={`approve-${i}`} onClick={approve} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#2C4C3B] text-white text-xs hover:bg-[#1A3326]"><CheckCircle2 className="w-3 h-3" /> Approve</button>
                )}
                {!isSuper && !isSelf && !isPending && (
                  <div className="flex items-center gap-2 flex-wrap">
                    {role !== "admin" && <button data-testid={`promote-admin-${i}`} onClick={() => setRole(u, "admin")} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#2C4C3B] text-white text-xs hover:bg-[#1A3326]"><ShieldCheck className="w-3 h-3" /> Admin</button>}
                    {role !== "staff" && <button data-testid={`promote-staff-${i}`} onClick={() => setRole(u, "staff")} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#D4A373] text-white text-xs hover:bg-[#b3854e]"><ShieldCheck className="w-3 h-3" /> Staff</button>}
                    {role !== "bible_school_student" && <button data-testid={`assign-student-${i}`} onClick={() => setRole(u, "bible_school_student")} className="px-3 py-1.5 rounded-full border border-blue-200 text-blue-900 text-xs hover:bg-blue-50">Bible School Student</button>}
                    {role !== "member" && <button data-testid={`demote-${i}`} onClick={() => setRole(u, "member")} className="px-3 py-1.5 rounded-full border border-[#E5E0D8] text-[#1C201E] text-xs hover:bg-[#F3EFEA]">Member</button>}
                    {role !== "visitor" && <button data-testid={`make-visitor-${i}`} onClick={() => setConfirmAction({ kind: "visitor", user: u })} className="px-3 py-1.5 rounded-full border border-sky-200 text-sky-900 text-xs hover:bg-sky-50">Change to Visitor</button>}
                    {role === "member" && verification === "Verified" && <button data-testid={`return-pending-${i}`} onClick={() => returnToPending(u)} className="px-3 py-1.5 rounded-full border border-amber-200 text-amber-900 text-xs hover:bg-amber-50">Return to Pending Verification</button>}
                  </div>
                )}
                {isSuper && <span className="text-xs text-[#5C6661]">Allowlisted</span>}
              </div>
            </div>
          );
        })}
        {users.length === 0 && <div className="text-sm text-[#5C6661]">No users yet.</div>}
      </div>
      {confirmAction && <div className="fixed inset-0 z-[120] bg-black/40 flex items-center justify-center p-5" role="presentation"><div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" role="dialog" aria-modal="true" aria-labelledby="role-confirm-title"><h4 id="role-confirm-title" className="font-serif-display text-2xl">{confirmAction.kind === "pending" ? "Return to Pending Verification?" : "Change this user to Visitor?"}</h4><p className="mt-3 text-sm text-[#5C6661]">{confirmAction.kind === "pending" ? "This member will no longer be marked as verified and will return to the verification queue. Their account and profile information will remain intact." : "The user will lose access to member-only features and retain only Visitor-level access. Existing account data will be preserved."}</p><div className="mt-6 flex justify-end gap-3"><button disabled={transitioning} onClick={() => setConfirmAction(null)} className="px-4 py-2 rounded-full border text-sm disabled:opacity-50">Cancel</button><button disabled={transitioning} onClick={confirmTransition} className="px-4 py-2 rounded-full bg-[#2C4C3B] text-white text-sm disabled:opacity-50">{confirmAction.kind === "pending" ? (transitioning ? "Returning…" : "Return to Pending") : "Change to Visitor"}</button></div></div></div>}
    </div>
  );
}

function MemberView({ user }) {
  const [counter, setCounter] = useState(5);
  const pwaUrl = (process.env.REACT_APP_WEB_URL || "https://bbcbarani.org").replace(/\/$/, "");

  useEffect(() => {
    if (counter > 0) {
      const timer = setTimeout(() => setCounter(counter - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      // Attempt deep link first
      const start = Date.now();
      window.location.href = "bbcconnect://home";

      // If the user hasn't left the page in 1.5 seconds, redirect to web URL
      setTimeout(() => {
        if (Date.now() - start < 2000) {
          window.location.href = pwaUrl;
        }
      }, 1500);
    }
  }, [counter, pwaUrl]);

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-6" data-testid="member-view">
      <div className="max-w-md w-full text-center p-10 bg-white border border-[#E5E0D8] rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.04)]">
        <div className="w-14 h-14 mx-auto rounded-full bg-[#F3EFEA] flex items-center justify-center">
          <UserIcon className="w-6 h-6 text-[#2C4C3B]" />
        </div>
        <h1 className="mt-6 font-serif-display text-3xl text-[#1C201E]">Welcome, {user.name?.split(" ")[0] || "friend"}</h1>
        <p className="mt-3 text-sm text-[#5C6661]">You&rsquo;re signed in as a member. Redirecting you to the BBC Connect app in {counter} seconds...</p>
        <div className="mt-8 space-y-3">
          <a href={pwaUrl} className="block w-full px-6 py-3 rounded-full bg-[#2C4C3B] text-white text-sm hover:bg-[#1A3326]">Go to App Now</a>
          <a href="/" className="block w-full px-6 py-3 rounded-full border border-[#E5E0D8] text-[#5C6661] text-sm hover:bg-[#F3EFEA]">Return to site</a>
        </div>
      </div>
    </div>
  );
}

function PendingView({ user, onProfile }) {
  const [profile, setProfile] = useState(user);
  const pwaUrl = (process.env.REACT_APP_WEB_URL || "https://bbcbarani.org").replace(/\/$/, "");
  useEffect(() => { api.get("/auth/me").then(({ data }) => setProfile(data)).catch(() => {}); }, []);
  const person = profile || user;
  const address = person?.address;
  const complete = Boolean(person?.birthday && person?.sex && person?.marital_status && person?.phones?.some(phone => phone?.trim()) && (typeof address === "string" ? address.trim() : address?.line1));

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12" data-testid="pending-view">
      <div className="p-8 md:p-10 bg-white border border-amber-200 rounded-3xl">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-medium uppercase tracking-widest">Pending Verification</div>
        <h1 className="mt-4 font-serif-display text-4xl text-[#1C201E]">Welcome, {person?.name?.split(" ")[0] || "friend"}</h1>
        {!complete ? <><h2 className="mt-4 font-serif-display text-2xl text-[#1C201E]">Are you a member of BBC Barani?</h2><p className="mt-2 text-[#5C6661] leading-relaxed text-sm">Complete your profile details so your membership can be reviewed and verified.</p></> : <><h2 className="mt-4 font-serif-display text-2xl text-[#1C201E]">Verification Pending</h2><p className="mt-2 text-[#5C6661] leading-relaxed text-sm">Your profile information has been submitted and is awaiting review.</p></>}
        <div className="mt-6 flex flex-wrap gap-3">
          {!complete && <a href="/profile" className="px-5 py-2 rounded-full bg-[#2C4C3B] text-white text-sm hover:bg-[#1A3326]">Complete Profile</a>}
          <a href={pwaUrl} className="px-5 py-2 rounded-full border border-[#E5E0D8] text-[#5C6661] text-sm">Open App</a>
          <a href="/" className="px-5 py-2 rounded-full text-sm text-[#5C6661] hover:text-[#2C4C3B]">Return to site</a>
        </div>
        <div className="mt-8 pt-6 border-t border-[#E5E0D8]">
          <div className="text-xs uppercase tracking-widest text-[#5C6661] mb-3">Your profile</div>
          <ProfilePhotoWidget user={person} onChange={onProfile} />
        </div>
      </div>
    </div>
  );
}


export default function AdminDashboard() {
  const location = useLocation();
  const { user, loading, logout } = useAuth();
  const active = user || location.state?.user;
  const role = active?.role || (active?.is_admin ? "super_admin" : "member");
  const [visitorUnread, setVisitorUnread] = useState(0);
  useEffect(() => {
    if (!["super_admin", "admin", "staff"].includes(role)) return;
    let mounted = true;
    const refresh = async () => {
      try {
        const { data } = await api.get("/visitor-messages/unread-count");
        if (mounted) setVisitorUnread(data.unread_count || 0);
      } catch { /* The inbox presents an error if opened. */ }
    };
    refresh();
    const timer = setInterval(refresh, 60000);
    return () => { mounted = false; clearInterval(timer); };
  }, [role]);

  const allTabs = [
    { key: "analytics", label: "Usage Analytics", roles: ["super_admin", "admin", "staff"] },
    { key: "content", label: "Page Content", roles: ["super_admin"] },
    { key: "livestream", label: "Livestream", roles: ["super_admin"] },
    { key: "offline_content", label: "Offline Content Versions", roles: ["super_admin", "admin"] },
    { key: "sermons", label: "Sermons", roles: ["super_admin", "admin", "staff"] },
    { key: "speakers", label: "Speakers", roles: ["super_admin", "admin", "staff"] },
    { key: "sermon_tags", label: "Sermon Tags", roles: ["super_admin", "admin", "staff"] },
    { key: "ministry_content", label: "Ministry Content", roles: ["super_admin", "admin", "staff"] },
    { key: "music_library", label: "Music Library", roles: ["super_admin", "admin", "staff"] },
    { key: "library", label: "Library / eBooks", roles: ["super_admin", "admin", "staff"] },
    { key: "events", label: "Events", roles: ["super_admin", "admin", "staff"] },
    { key: "daily_verse", label: "Daily Verse", roles: ["super_admin", "admin", "staff"] },
    { key: "devotion", label: "Devotion", roles: ["super_admin", "admin", "staff"] },
    { key: "announcements", label: "Announcements", roles: ["super_admin", "admin", "staff"] },
    { key: "prayers", label: "Prayer & Praise", roles: ["super_admin", "admin", "staff"] },
    { key: "visitor_messages", label: "Visitors Messages", roles: ["super_admin", "admin", "staff"] },
    { key: "counseling", label: "Counseling", roles: ["super_admin", "staff"] },
    { key: "music", label: "Music Stations", roles: ["super_admin", "admin"] },
    { key: "news_feed", label: "News Feed", roles: ["super_admin"] },
    { key: "tts", label: "TTS Manager", roles: ["super_admin", "admin"] },
    { key: "mobile_app", label: "Mobile App", roles: ["super_admin", "admin"] },
    { key: "groups", label: "Group Chats", roles: ["super_admin", "admin"] },
    { key: "messages_visibility", label: "Messages Visibility", roles: ["super_admin"] },
    { key: "ai", label: "AI Manager", roles: ["super_admin", "admin"] },
    { key: "quiz", label: "Challenges & Rankings", roles: ["super_admin", "admin", "staff"] },
    { key: "bible", label: "Bible Content", roles: ["super_admin"] },
    { key: "doctrinal_review", label: "Doctrinal Review", roles: ["super_admin", "admin"] },
    { key: "backups", label: "Backups", roles: ["super_admin"] },
    { key: "users", label: "Users", roles: ["super_admin"] },
  ];
  const navigationGroups = [
    { label: "Insights", Icon: BarChart3, keys: ["analytics"] },
    { label: "Content", Icon: FileText, keys: ["content", "daily_verse", "devotion", "sermons", "ministry_content", "events", "announcements", "news_feed", "library"] },
    { label: "Bible", Icon: BookOpen, keys: ["bible", "doctrinal_review"] },
    { label: "Bible Challenge", Icon: Trophy, keys: ["quiz"] },
    { label: "Communication", Icon: MessageSquare, keys: ["visitor_messages", "prayers", "groups", "counseling"] },
    { label: "Media", Icon: Radio, keys: ["music_library", "music", "speakers", "sermon_tags"] },
    { label: "Users", Icon: UsersIcon, keys: ["users"] },
    { label: "System / Administration", Icon: Settings, keys: ["livestream", "offline_content", "mobile_app", "tts", "ai", "messages_visibility", "backups"] },
  ];
  const tabs = allTabs.filter((t) => t.roles.includes(role));
  const [tab, setTab] = useState(tabs[0]?.key || "sermons");
  const [meUser, setMeUser] = useState(active);
  useEffect(() => {
    if (tabs.length && !tabs.find((t) => t.key === tab)) setTab(tabs[0].key);
  }, [role]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setMeUser(active); }, [active]);

  if (loading && !location.state?.user) return <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6] text-[#5C6661]">Loading…</div>;
  if (!active) return <Navigate to="/admin/login" replace />;

  const roleLabel = { super_admin: "Super Admin", admin: "Admin", staff: "Staff", bible_school_student: "Bible School Student", member: "Member", pending: "Pending" }[role] || "Member";
  const currentTab = tabs.find(item => item.key === tab);

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="admin-dashboard">
      <a href="#cms-main" className="skip-link">Skip to CMS workspace</a>
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#E5E0D8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 min-h-16 py-2 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 text-[#2C4C3B]">
            <img src={BBC_LOGO_URL} alt="BBC Barani seal" className="w-8 h-8 object-contain" />
            <span className="font-serif-display text-lg sm:text-xl"><span className="hidden sm:inline">BBC Barani &middot; </span>CMS</span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <a href="/" data-testid="view-site-link" aria-label="View public website" className="inline-flex min-h-11 items-center gap-2 rounded-full px-3 text-sm text-[#5C6661] hover:bg-[#F3EFEA] hover:text-[#2C4C3B]"><ExternalLink aria-hidden="true" className="h-4 w-4"/><span className="hidden sm:inline">View site</span></a>
            <span data-testid="user-role-badge" className={`hidden sm:inline text-xs px-3 py-1 rounded-full font-medium ${role === "super_admin" ? "bg-[#2C4C3B] text-white" : role === "admin" ? "bg-[#D4A373]/30 text-[#8a5a2a]" : role === "staff" ? "bg-[#D4A373]/20 text-[#8a5a2a]" : role === "pending" ? "bg-amber-100 text-amber-800 border border-amber-300" : "bg-[#E5E0D8] text-[#1C201E]"}`}>{roleLabel}</span>
            <span className="text-sm text-[#5C6661] hidden md:inline">{(meUser || active)?.email}</span>
            <NotificationBell />
            <ProfilePhotoWidget user={meUser || active} onChange={setMeUser} compact />
            <button data-testid="logout-button" aria-label="Log out" onClick={async () => { await logout(); window.location.href = "/admin/login"; }} className="inline-flex min-h-11 items-center gap-2 px-3 py-1.5 rounded-full border border-[#E5E0D8] text-sm hover:bg-[#F3EFEA]">
              <LogOut aria-hidden="true" className="w-4 h-4" /> <span className="hidden sm:inline">Log out</span>
            </button>
          </div>
        </div>
      </header>

      {role !== "visitor" && (active.verification_status === "pending" || role === "pending") ? (
        <PendingView user={meUser || active} onProfile={setMeUser} />
      ) : role === "visitor" ? (
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12"><div className="p-8 bg-white border border-[#E5E0D8] rounded-3xl"><span className="text-xs uppercase tracking-widest text-[#5C6661]">Visitor</span><h1 className="mt-3 font-serif-display text-3xl">Welcome to BBC Connect</h1><p className="mt-3 text-sm text-[#5C6661]">Your Visitor account can access the Bible, Daily Verse, and Daily Devotion.</p><div className="mt-6 flex gap-3"><a className="px-5 py-2 rounded-full bg-[#2C4C3B] text-white text-sm" href="/">Home</a><a className="px-5 py-2 rounded-full border text-sm" href="/profile">Profile</a></div></div></div>
      ) : role === "member" || role === "bible_school_student" ? (
        <MemberView user={meUser || active} />
      ) : (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-6 lg:py-8">
          <div className="mb-6 lg:hidden">
            <label htmlFor="cms-section" className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-[#5C6661]">CMS section</label>
            <select id="cms-section" value={tab} onChange={event => setTab(event.target.value)} className="min-h-12 w-full rounded-xl border border-[#CFC8BE] bg-white px-4 text-base font-medium text-[#1C201E] shadow-sm">
              {navigationGroups.map(({ label, keys }) => {
                const permitted = tabs.filter(item => keys.includes(item.key));
                return permitted.length ? <optgroup key={label} label={label}>{permitted.map(item => <option key={item.key} value={item.key}>{item.label}{item.key === "visitor_messages" && visitorUnread > 0 ? ` (${visitorUnread} new)` : ""}</option>)}</optgroup> : null;
              })}
            </select>
          </div>
          <div className="grid gap-6 lg:grid-cols-[230px_minmax(0,1fr)]">
          <nav aria-label="CMS navigation" className="hidden lg:block space-y-2 lg:sticky lg:top-24 lg:self-start lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto lg:pr-1">
            {navigationGroups.map(({ label, Icon, keys }) => {
              const permitted = tabs.filter(item => keys.includes(item.key));
              if (!permitted.length) return null;
              const isActive = keys.includes(tab);
              return <details key={label} open={isActive || undefined} className="rounded-xl border border-[#E5E0D8] bg-white">
                <summary className={`flex cursor-pointer items-center gap-2 p-3 text-sm font-semibold ${isActive ? "text-[#2C4C3B] bg-[#F3EFEA]" : "text-[#5C6661]"}`}><Icon className="h-4 w-4"/>{label}<ChevronDown className="ml-auto h-4 w-4"/></summary>
                <div className="grid gap-1 p-2">{permitted.map(item => <button key={item.key} data-testid={`tab-${item.key}`} aria-current={tab === item.key ? "page" : undefined} onClick={() => setTab(item.key)} className={`min-h-11 rounded-lg px-3 py-2 text-left text-sm ${tab === item.key ? "bg-[#2C4C3B] text-white" : "text-[#1C201E] hover:bg-[#F3EFEA]"}`}>{item.label}{item.key === "visitor_messages" && visitorUnread > 0 && <span className="ml-2 rounded-full bg-[#D4A373] px-2 text-xs">{visitorUnread}</span>}</button>)}</div>
              </details>;
            })}
          </nav>
          <main id="cms-main" tabIndex="-1" className="min-w-0" aria-label={currentTab?.label}>
          <div className="mb-5">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#8A5A2A]">CMS workspace</p>
            <h1 className="mt-1 font-serif-display text-3xl sm:text-4xl text-[#1C201E]">{currentTab?.label}</h1>
          </div>
          {tab === "analytics" && <AnalyticsTab />}
          {tab === "livestream" && role === "super_admin" && <ContentTab key="livestream" initialSection="livestream" />}
          {tab === "offline_content" && <OfflineContentTab/>}
          {tab === "content" && role === "super_admin" && <ContentTab />}
          {tab === "sermons" && <SermonCMS />}
          {tab === "speakers" && <ManagedSermonEntities kind="speakers" />}
          {tab === "sermon_tags" && <ManagedSermonEntities kind="tags" />}
          {tab === "ministry_content" && <MinistryCmsTab section="content" />}
          {tab === "music_library" && <MinistryCmsTab section="music" />}
          {tab === "library" && <LibraryCMS />}
          {tab === "events" && <EventsTab />}
          {tab === "daily_verse" && <DailyVerseTab />}
          {tab === "devotion" && <DevotionTab />}
          {tab === "announcements" && <AnnouncementsTab />}
          {tab === "prayers" && <PrayersTab role={role} />}
          {tab === "visitor_messages" && <VisitorMessagesTab role={role} onUnreadChange={setVisitorUnread} />}
          {tab === "counseling" && <CounselingTab role={role} user={active} />}
          {tab === "groups" && <GroupsTab currentUser={active} />}
          {tab === "messages_visibility" && role === "super_admin" && <MessagesVisibilityTab />}
          {tab === "mobile_app" && <MobileAppTab />}
          {tab === "music" && <MusicTab />}
          {tab === "news_feed" && role === "super_admin" && <NewsFeedTab />}
          {tab === "tts" && (role === "super_admin" || role === "admin") && <TTSTab />}
          {tab === "ai" && (role === "super_admin" || role === "admin") && <AiTab role={role} />}
          {tab === "quiz" && (role === "super_admin" || role === "admin" || role === "staff") && <QuizTab role={role} />}
          {tab === "bible" && role === "super_admin" && <BibleWarmTab />}
          {tab === "doctrinal_review" && (role === "super_admin" || role === "admin") && <DoctrinalReviewTab />}
          {tab === "backups" && role === "super_admin" && <BackupsTab />}
          {tab === "users" && role === "super_admin" && <UsersTab currentUser={active} />}
          </main>
          </div>
        </div>
      )}
    </div>
  );
}
